import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../../components/contexts/AuthContext';
import { fetchVehicleV1Variants } from '../../../components/qv4/models/shared/customerVehiclesV1Model';
import { listVehicleCatalogue, vehicleErrorMessage } from '../../../components/qv4/models/shared/vehicleCatalogueModel';

export function useVehicleVariants(familyId, { managed = false, savedId, savedName } = {}) {
  const actor = useContext(AuthContext)?.authState?.id;
  const [search, setSearch] = useState(''), [paging, setPaging] = useState({});
  const [revision, setRevision] = useState(0), [state, setState] = useState({}), [saved, setSaved] = useState({});
  const scope = JSON.stringify([actor, familyId, managed, search, revision]);
  const page = paging.scope === scope ? paging.page : 1;
  useEffect(() => {
    if (!familyId) return undefined;
    let active = true;
    setState(current => ({ ...current, loading: true, error: '' }));
    const timer = setTimeout(() => {
      const request = managed ? listVehicleCatalogue('variants', { family_id: familyId, search, page, limit: 50, status: 'active' }) : fetchVehicleV1Variants({ familyId, search, page, limit: 50 });
      request.then(data => {
        if (active) setState(current => ({ scope, page, rows: [...new Map([...(page > 1 && current.scope === scope ? current.rows : []), ...data.rows].map(row => [row.id, row])).values()], total: data.total, loading: false }));
      }).catch(error => { if (active) setState({ scope, page, rows: [], total: 0, error: vehicleErrorMessage(error), loading: false }); });
    }, search ? 300 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [actor, familyId, managed, search, page, scope]);
  // V1 deliberately exposes no variant status. Search its exact saved name via
  // the active picker; absence, not an arbitrary first page, establishes hidden.
  useEffect(() => {
    if (!familyId || !savedId || !savedName) return undefined;
    let active = true;
    const lookup = async () => {
      let page = 1, loaded = 0;
      while (active) {
        const data = await (managed ? listVehicleCatalogue('variants', { family_id: familyId, search: savedName, status: 'active', limit: 200, page }) : fetchVehicleV1Variants({ familyId, search: savedName, limit: 200, page }));
        if (data.rows.some(row => Number(row.id) === Number(savedId))) return false;
        loaded += data.rows.length;
        if (!data.rows.length || loaded >= data.total) return true;
        page += 1;
      }
      return false;
    };
    lookup().then(hidden => { if (active) setSaved({ familyId, savedId, hidden }); })
      .catch(() => { if (active) setSaved({ familyId, savedId, unavailable: true }); });
    return () => { active = false; };
  }, [actor, familyId, managed, savedId, savedName, revision]);
  const rows = state.scope === scope ? state.rows || [] : [];
  const options = rows.map(row => ({ value: Number(row.id), label: row.name }));
  if (savedId && !options.some(row => row.value === Number(savedId))) {
    const known = saved.familyId === familyId && saved.savedId === savedId;
    options.unshift({ value: Number(savedId), label: `${savedName || 'Saved variant'}${known && saved.hidden ? ' (hidden)' : ''}`, disabled: !known || saved.hidden || saved.unavailable });
  }
  const currentPage = state.scope === scope && state.page === page;
  return { options, search, setSearch, loading: !currentPage || state.loading, error: state.scope === scope ? state.error : '',
    hasMore: currentPage && rows.length < (state.total || 0), loadMore: () => { if (currentPage && !state.loading && !state.error) setPaging({ scope, page: page + 1 }); }, reload: () => setRevision(value => value + 1) };
}
